/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import qs from 'qs';
import useSWR from 'swr';

import type * as Type from '@/common/interface';
import request from '@/utils/request';

export const useAIFindDrafts = (params: Type.AIFindDraftReq) => {
  const apiUrl = `/answer/admin/api/aifind/drafts?${qs.stringify(params, {
    skipNulls: true,
  })}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.AIFindDraftItem>,
    Error
  >([apiUrl], request.instance.get);
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const useAIFindDraftDetail = (id?: string) => {
  const apiUrl = id
    ? `/answer/admin/api/aifind/drafts/detail?id=${encodeURIComponent(id)}`
    : null;
  const { data, error, mutate } = useSWR<Type.AIFindDraftItem, Error>(
    apiUrl ? [apiUrl] : null,
    request.instance.get,
  );
  return {
    data,
    isLoading: Boolean(id) && !data && !error,
    error,
    mutate,
  };
};

export const updateAIFindDraft = (params: Type.AIFindDraftUpdateReq) => {
  return request.put('/answer/admin/api/aifind/drafts', params);
};

export const publishAIFindDraft = (id: string) => {
  return request.post<Type.AIFindDraftItem>(
    '/answer/admin/api/aifind/drafts/publish',
    { id },
  );
};

export const ignoreAIFindDraft = (id: string) => {
  return request.post('/answer/admin/api/aifind/drafts/ignore', { id });
};

export const mergeAIFindDraft = (id: string, questionID: string) => {
  return request.post('/answer/admin/api/aifind/drafts/merge', {
    id,
    question_id: questionID,
  });
};

export const deleteAIFindDraft = (id: string) => {
  return request.delete('/answer/admin/api/aifind/drafts', { id });
};
